import React, { useRef, useState, useMemo } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

import { DISABLE_SWIPE } from '../utilities/enums';
import { useOverflowObserver, isTrainingMode } from '../helpers';
import { getNOTAMListFromAllData } from '../utilities/helperfunctions';

const ENROUTE_AIRPORT = 'ENROUTE_AIRPORT';
const AREA = 'AREA';

const styles = {
  container: {
    overflowX: 'auto',
  },
  content: {
    width: 'fit-content',
  },
  pre: {
    marginBottom: '16px',
    marginTop: 0,
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '16px',
  },
};

const getTrainingData = (data) => {
  if (Array.isArray(data?.training) && data.training.length > 0) {
    return data.training[0];
  }
  if (data?.adList) {
    return Array.isArray(data.adList) ? data.adList[0] : data.adList;
  }
  return null;
};

const extractNotamContent = (notam) => {
  if (!notam?.sNOTAM) {
    return null;
  }
  return Array.isArray(notam.sNOTAM) ? notam.sNOTAM[0] : notam.sNOTAM;
};

const getSectionName = (notam) => {
  const sectionName = notam?.sectionName;
  return Array.isArray(sectionName) ? sectionName[0] : sectionName;
};

const shouldShowSuitabilityWarning = (notam) => {
  return notam?.bShowSuitabilityCheckWarning?.[0] === 'true';
};

const getEnrouteAirportNotams = (data) => {
  const notamList = getNOTAMListFromAllData(data);

  // Filter by sectionName = "ENR ALT"
  const filteredList = notamList.filter(
    (notam) => getSectionName(notam) === 'ENR ALT'
  );

  const showWarning = filteredList.some(shouldShowSuitabilityWarning);

  return {
    content: filteredList
      .map((notam) => extractNotamContent(notam))
      .filter(Boolean)
      .join('\n\n'),
    showWarning,
  };
};

const getAreaNotams = (data) => {
  const notamList = getNOTAMListFromAllData(data);

  // Filter by sectionName = "Area Notams"
  const filteredList = notamList.filter(
    (notam) => getSectionName(notam) === 'Area Notams'
  );

  const showWarning = filteredList.some(shouldShowSuitabilityWarning);

  return {
    content: filteredList
      .map((notam) => extractNotamContent(notam))
      .filter(Boolean)
      .join('\n\n'),
    showWarning,
  };
};

const getNotamSections = (data, trainingMode) => {
  if (trainingMode) {
    const trainingData = getTrainingData(data);

    if (trainingData?.enRouteNOTAM?.[0]) {
      // In training mode, show all as enroute airport notam
      return {
        enrouteAirport: { content: trainingData.enRouteNOTAM[0], showWarning: false },
        area: null,
      };
    }

  }

  const enrouteResult = getEnrouteAirportNotams(data);
  const areaResult = getAreaNotams(data);

  return {
    enrouteAirport: enrouteResult.content ? enrouteResult : null,
    area: areaResult.content ? areaResult : null,
  };
};

export const NotamNew = (props) => {
  const trainingMode = isTrainingMode();
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [isSwipeDisabled, setIsSwipeDisabled] = useState(false);
  const [viewMode, setViewMode] = useState(ENROUTE_AIRPORT);

  useOverflowObserver(containerRef, contentRef, setIsSwipeDisabled);

  const { enrouteAirport, area } = useMemo(
    () => getNotamSections(props.data, trainingMode),
    [props.data, trainingMode]
  );

  const handleToggle = (event, newValue) => {
    if (newValue !== null) {
      setViewMode(newValue);
    }
  };

  const hasContent = enrouteAirport || area;
  const currentSection = viewMode === ENROUTE_AIRPORT ? enrouteAirport : area;
  const currentContent = currentSection?.content;
  const showWarning = currentSection?.showWarning;

  return (
    <div className={'content-container'}>
      {hasContent && (
        <div style={styles.toggleContainer}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleToggle}
          >
            <ToggleButton value={ENROUTE_AIRPORT} disabled={!enrouteAirport}>
              Enroute Airport
            </ToggleButton>
            <ToggleButton value={AREA} disabled={!area}>
              Area
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      )}

      {showWarning && (
        <div
          style={{
            color: 'red',
            fontWeight: 600,
            marginBottom: '16px',
          }}
        >
          WX/NOTAM SUITABILITY CHECK BY FLIGHT PLANNING SYSTEM NOT PERFORMED FOR THIS AERODROME
        </div>
      )}

      <div
        ref={containerRef}
        className={isSwipeDisabled ? DISABLE_SWIPE : ''}
        style={styles.container}
      >
        <div ref={contentRef} style={styles.content}>
          {!hasContent && <p>NOTAM data not found</p>}
          {currentContent && <pre style={styles.pre}>{currentContent}</pre>}
        </div>
      </div>
    </div>
  );
};
